import { defaults, createHook, createStore, createStateHook, createActionsHook } from 'react-sweet-state';
import type { Action } from 'react-sweet-state';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import uniq from 'lodash/uniq';
import omit from 'lodash/omit';
import flatMap from 'lodash/flatMap';
import mergeWith from 'lodash/mergeWith';
import debounce from 'lodash/debounce';
import type { Promotion, FilterOption } from '@css-xp/graphtypes/src/generated-types-new';
import { Currency } from '@css-xp/graphtypes/src/generated-types-new';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { PricingPlanType } from '@atlassian/cssxp-ccp-types';
import { getPartnerPromotions } from './utils/get-partner-promotions';
import {
  getProductIdsOfferingId,
  processBillEstimateResponse,
  stitchChangeKeyObjects,
  getSelectedProductsAppsObj,
  getRemovedProductsAndAppsFromStore,
  getAppsIdsForSelectedContainer,
  getSelectedAppsData,
  getSelectedAppsObj,
  groupByCloudSiteOrOrgName,
  isCloudSiteOrOrganisationSelected,
  isNewEntitlement,
  getSandboxProductIdsInCart,
  getParentIdForSandboxProductInCart,
  getSelectedBillingProfile,
  getSelectedAppsDetails,
} from './utils';
import { AppPromotionResponse, State, CurrentScreenTypeEnum, FeatureFlags, EnrichmentStatus } from './types';
import { getProductLineItems, createProductLineItems } from './selectors/product-line-items-selector';
import { getProductAndAppsLineItems } from './selectors/product-apps-line-items-selector';
import { getLineItemsActualEstimate } from './selectors/line-items-actual-estimate-selector';
import { getLineItemsRecurringEstimate } from './selectors/line-items-recurring-estimate-selector';
import { SelectType } from '../ui/billing-profile/src/types';
import {
  getCloudSiteOrOrgNameMap,
  getExternalCloudSiteOrOrgNameMap,
  getTransferCloudSiteOrOrgNameMap,
} from './selectors/cloud-site-map-selector';
import {
  CartLinkedEntitlementsSchema,
  CartProductSchema,
  ValidationErrorSchema,
  BillToInfo,
  CollectionToInstanceOfferingMapResponse,
} from '../types';
import { OperationType, LinkedEntitlementRelationShipType } from '@css-xp/ppc-purchase-experience';
import { getPricingPlanOptions } from './selectors/pricing-plan-options-selector';
import { BillEstimateType, getBillEstimate } from './utils/get-bill-estimate';
import { getCurrentOperationTypesSelector } from './selectors/get-current-operation-type-selector';
import { getAppliedPromotions } from './selectors/applied-promotions-selector';
import { getNativeProductsWithOfferingsAndPricingPlans } from './utils/get-native-products-with-offerings-and-pricing-plans';
import { getProductIdsWithoutChanges } from './utils/get-product-ids-without-changes';
import { CartPromotionAction } from '../ui/product-line-items/src/types';
import { getTimestampFromDate } from '../ui/product-line-items/src/components/match-bill-dates';
import { getActiveUserCountForContainerOrProductId } from './utils/get-active-user-for-container-or-product-id';
import { getDefaultBillingProfile } from './utils/get-default-billing-profile';
import { getIsLatestBillEstimationFetched } from './utils/get-latest-bill-estimation-fetched';
import { getSandboxLinkedEntitlementInCart } from './utils/get-sandbox-linked-entitlement-in-cart';
import { getUpdatedEntitlementWithPromotions } from './utils/get-updated-entitlement-with-promotions';
import { getAvailableProductIdsForAddAllToCart } from './utils/get-available-product-ids-for-add-all-to-cart';
import { getValidNextBillDateTimeStamp } from './utils/get-valid-next-bill-date-timestamp';
import { getSandboxEntitlementsLinkedToProducts } from './utils/get-sandbox-oms-payload';
import { getNextBillDateForNewEntitlements } from './utils/get-next-bill-date-for-new-entitlements';
import { getPricingPlanEligibility } from './selectors/pricing-plan-eligibility-selector';
import { getUpdatedAddons } from './utils/get-updated-addons';
import { getOrgProductInstancesMap } from './selectors/organisation-product-selector';
import { getCollectionInstanceProductIdOfferingIdMap } from './utils/collections-offerings-utils';
import { getUpdatedSelectedInstancesOffering } from './utils/get-updated-selected-instances-offering';

defaults.devtools = true;

type ProductsAppsList = Pick<State, 'products' | 'linkedEntitlements'> &
  Partial<
    Pick<State, 'sandboxApps' | 'sandboxProducts' | 'inactiveProducts' | 'inactiveApps' | 'inactiveAddons' | 'addons'>
  >;

const delayBillEstimate = debounce((actions: any, dispatch: any) => {
  dispatch(actions.getBillEstimate());
}, 1000);

// FIXME:: initialize store is called multiple in code so add only those keys
//  here which are changing and need default values, e.g. don't add featureFlags here as it will remove the featureFlags set explicitly.
export const initialState: State = {
  sidebarFilter: {
    searchQuery: '',
    billingProfileIds: [],
  },
  isLoading: false,
  isRefreshEstimateLoading: false,
  invoiceGroupId: '',
  manualSelectedInvoiceGroupId: '',
  invoiceGroupName: '',
  products: {},
  addons: {},
  inactiveAddons: {},
  linkedEntitlements: {},
  sandboxApps: {},
  sandboxProducts: {},
  inactiveProducts: {},
  inactiveApps: {},
  selectedProducts: {},
  selectedLinkedEntitlements: {},
  partnerPromotions: {},
  validationErrors: null,
  nextBillDate: {},
  currentScreen: undefined,
  recurringBillEstimate: null,
  // estimated users for recurring bill estimate
  // https://hello.atlassian.net/wiki/spaces/COMITFO/pages/3403322557/Allow+users+column+to+edit+in+UOF+cart+page
  estimatedUsersForRecurringBillEstimate: {},
  partnerPromotionBenefits: {},
};

export const actions = {
  initializeStore: (productAppList: ProductsAppsList): Action<State> => ({ setState }) => {
    setState({
      ...initialState,
      ...productAppList,
    });
  },

  setSearchQuery: (searchQuery: string): Action<State> => ({ getState, setState }) => {
    const state = getState();

    setState({
      ...state,
      sidebarFilter: {
        ...state.sidebarFilter,
        billingProfileIds: state.sidebarFilter?.billingProfileIds ?? [],
        searchQuery,
      },
    });
  },

  setSidebarFilter: ({
    searchQuery,
    billingProfileIds,
  }: {
    searchQuery?: string;
    billingProfileIds?: string[];
  }): Action<State> => ({ getState, setState }) => {
    const state = getState();

    searchQuery = searchQuery ?? (state.sidebarFilter?.searchQuery || '');

    const defaultBillingProfileIds = state.sidebarFilter?.billingProfileIds || [];
    // checking for `undefined` because a user might want to clear the billing profile ids by sending an empty array
    billingProfileIds = billingProfileIds === undefined ? defaultBillingProfileIds : billingProfileIds;

    setState({
      ...state,
      sidebarFilter: {
        billingProfileIds,
        searchQuery,
      },
    });
  },

  resetStore: (): Action<State> => ({ setState, getState }) => {
    // merging with existing state so as to reset ephemeral data like products, apps, selectedProductIds etc. while keeping more permanent data like `selectedPartnerAccountData` in the store
    setState({ ...getState(), ...initialState });
  },
  addProductsAndApps: (newProductAppList: ProductsAppsList): Action<State> => ({ setState, getState }) => {
    const state = getState();
    setState({
      ...state,
      products: { ...state.products, ...newProductAppList.products },
      linkedEntitlements: { ...state.linkedEntitlements, ...newProductAppList.linkedEntitlements },
      sandboxApps: { ...state.sandboxApps, ...newProductAppList.sandboxApps },
      sandboxProducts: { ...state.sandboxProducts, ...newProductAppList.sandboxProducts },
      inactiveProducts: { ...state.inactiveProducts, ...newProductAppList.inactiveProducts },
      inactiveApps: { ...state.inactiveApps, ...newProductAppList.inactiveApps },
      inactiveAddons: { ...state.inactiveAddons, ...newProductAppList.inactiveAddons },
      addons: { ...state.addons, ...newProductAppList.addons },
    });
  },

  removeProductsAndApps: ({
    entitlementNumber,
    siteURL,
    removeOnlyTransferEntitlements = false,
  }: {
    entitlementNumber?: string;
    siteURL?: string;
    removeOnlyTransferEntitlements?: boolean;
  }): Action<State> => ({ setState, getState }) => {
    if (!entitlementNumber && !siteURL) return;
    const state = getState();
    const products = { ...state.products };
    const linkedEntitlements = { ...state.linkedEntitlements };
    const selectedProducts = { ...state.selectedProducts };
    const selectedLinkedEntitlements = { ...state.selectedLinkedEntitlements };
    const showSandboxProducts =
      state.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS] || false;
    const isSandboxDecouplingEnabled = state.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_DECOUPLING] || false;

    let cloudSiteUrlOfEntitlement;
    if (entitlementNumber) {
      cloudSiteUrlOfEntitlement = Object.values(products).find(
        (product) => product?.entitlementNumber === entitlementNumber
      )?.cloudSite;
    }

    const siteUrlToRemove = entitlementNumber ? cloudSiteUrlOfEntitlement : siteURL;

    Object.values(products).forEach((product) => {
      if (removeOnlyTransferEntitlements) {
        if (product?.cloudSite === siteUrlToRemove && product?.operationType === OperationType.TRANSFER) {
          delete products[product.id];
          if (showSandboxProducts && !isSandboxDecouplingEnabled) {
            /**
             * This is to remove product and its sandbox product when transfer entitlement is re-searched
             *
             * If any sandbox product present, remove in selectedProduct.
             * If any parent to sandbox product present, remove in selectedProduct.
             */
            const productIdOrContainerId = product?.containerId ?? product.id;
            const allSandboxProductIdsInCart = getSandboxProductIdsInCart(products, productIdOrContainerId);
            allSandboxProductIdsInCart.forEach((sandboxProductId) => {
              delete products[sandboxProductId];
              if (selectedProducts[sandboxProductId]) {
                delete selectedProducts[sandboxProductId];
              }
            });
            // If product is a sandbox product, remove its parent product in selectedProductIdSet
            const sandboxParentEntOrContainerId = product?.sandboxParent;
            if (sandboxParentEntOrContainerId) {
              const sandboxParentProductId = getParentIdForSandboxProductInCart(
                products,
                sandboxParentEntOrContainerId
              );
              sandboxParentProductId.forEach((sandboxParentId) => {
                delete products[sandboxParentId];
                if (selectedProducts[sandboxParentId]) {
                  delete selectedProducts[sandboxParentId];
                }
              });
            }
          }
          if (selectedProducts[product.id]) {
            delete selectedProducts[product.id];
          }
        }
      } else {
        if (product?.cloudSite === siteUrlToRemove) {
          delete products[product.id];
          if (selectedProducts[product.id]) {
            delete selectedProducts[product.id];
          }
        }
      }
    });

    const allApps = Object.values(linkedEntitlements).flatMap((item) => item);

    allApps.forEach((app) => {
      if (removeOnlyTransferEntitlements) {
        if (app?.cloudSite === siteUrlToRemove && app?.operationType === OperationType.TRANSFER) {
          delete linkedEntitlements[app.containerOrProductId];
          if (selectedLinkedEntitlements[app.id]) {
            delete selectedLinkedEntitlements[app.id];
          }
        }
      } else {
        if (app?.cloudSite === siteUrlToRemove) {
          delete linkedEntitlements[app.containerOrProductId];
          if (selectedLinkedEntitlements[app.id]) {
            delete selectedLinkedEntitlements[app.id];
          }
        }
      }
    });

    setState({
      ...state,
      products,
      linkedEntitlements,
      selectedProducts,
      selectedLinkedEntitlements,
    });
  },

  addNewProductToCart: (
    newProductList: CartProductSchema[],
    newAppList: CartLinkedEntitlementsSchema[]
  ): Action<State> => ({ setState, getState, dispatch }) => {
    const state = getState();
    const estimatedUsersForRecurringBillEstimate = { ...state.estimatedUsersForRecurringBillEstimate };
    const newProductsMergedById = keyBy(newProductList, 'id');
    // eslint-disable-next-line @typescript-eslint/ban-types
    let selectedProductsObj = newProductList.reduce((obj: Record<string, {}>, product) => {
      obj[product.id] = {};
      return obj;
    }, {});
    const linkedEntitlements = { ...state.linkedEntitlements };
    let selectedAppsObj = {};
    newAppList?.forEach((app) => {
      /**
       * Extracting promotions from new app list and moving promotions details to selectedLinkedEntitlements
       * Removing all promotions from main app list because promotions are editable in the store
       */
      selectedAppsObj = { ...selectedAppsObj, [app.id]: app.promotions?.length ? { promotions: app.promotions } : {} };
      app.promotions = []; // Removing all promotions from main app list because promotions are editable

      // Checking If parent subscription is already added in the cart or not
      if (app?.parentEntitlementId && !state.selectedProducts[app.parentEntitlementId]) {
        selectedProductsObj = { ...selectedProductsObj, [app.parentEntitlementId]: {} };
      }
    });

    const newAppsGroupedByContainerOrProductId = groupBy(newAppList, 'containerOrProductId');
    const containerOrProductIds = Object.keys(newAppsGroupedByContainerOrProductId);
    const mergedApps = mergeWith(linkedEntitlements, newAppsGroupedByContainerOrProductId, (destObj, newValue) =>
      destObj?.concat(newValue)
    );

    setState({
      ...state,
      products: { ...state.products, ...newProductsMergedById },
      linkedEntitlements: { ...mergedApps },
      selectedProducts: { ...state.selectedProducts, ...selectedProductsObj },
      selectedLinkedEntitlements: { ...state.selectedLinkedEntitlements, ...selectedAppsObj },
      estimatedUsersForRecurringBillEstimate,
    });

    dispatch(actions.setAppsEstimatedUsersForContainerOrProductId(containerOrProductIds));
    dispatch(actions.markBillEstimatesStale());
  },
  updateEntitlementPromotions: ({
    identifier,
    promotion,
    promotionId,
    promotionAction,
    isApp,
  }: {
    identifier: string;
    promotion?: AppPromotionResponse;
    promotionId?: string;
    promotionAction: CartPromotionAction;
    isApp?: boolean;
  }): Action<State> => ({ setState, getState, dispatch }) => {
    const state = getState();
    const updatedStateData = getUpdatedEntitlementWithPromotions({
      identifier,
      promotion,
      promotionId,
      promotionAction,
      isApp,
      state,
    });

    setState({
      ...state,
      ...updatedStateData,
    });
    dispatch(actions.markBillEstimatesStale([identifier]));
  },

  addExistingAppPromotions: ({
    id,
    appliedPromotions = [],
  }: {
    id: string;
    appliedPromotions?: Promotion[];
  }): Action<State> => ({ setState, getState, dispatch }) => {
    const state = getState();
    const existingApps = { ...state.linkedEntitlements };
    const appDetail = Object.values(state?.linkedEntitlements)
      ?.flatMap((item) => item)
      ?.find((app) => app.id === id);
    // updating the existing app with applied promotions
    if (appDetail) {
      appDetail.promotions = appliedPromotions;
      const selectedProductApps = existingApps?.[appDetail.containerOrProductId];
      selectedProductApps?.map((app) => {
        if (app.id === id) {
          app.promotions = appliedPromotions;
        }
      });
      // update the apps state with updated apps data
      setState({
        ...state,
        linkedEntitlements: {
          ...existingApps,
          [appDetail.containerOrProductId]: selectedProductApps,
        },
      });
      delayBillEstimate(actions, dispatch);
    }
  },

  setSelectedCustomer: (selectedCustomer: { name: string; ids: string[] } | null): Action<State> => ({ setState }) => {
    setState({ selectedCustomer });
  },

  setInvoiceGroup: (invoiceGroupId: string): Action<State> => ({ setState }) => {
    setState({ invoiceGroupId });
  },
  setManualInvoiceGroupId: (invoiceGroupId: string): Action<State> => ({ setState }) => {
    setState({ manualSelectedInvoiceGroupId: invoiceGroupId });
  },
  setInvoiceGroupName: (invoiceGroupName: string): Action<State> => ({ setState }) => {
    setState({ invoiceGroupName });
  },
  setCurrentScreen: (currentScreen: CurrentScreenTypeEnum): Action<State> => ({ setState }) => {
    setState({ currentScreen });
  },
  addAllToCart: (): Action<State> => ({ getState, setState, dispatch }) => {
    const state = getState();

    const allProductIds = Object.keys(state.products);

    /* Get all available product ids for add all to cart
     * Where we applied filter on the billing profile and search query
     */
    const availableProductIdsForAddAllToCart = getAvailableProductIdsForAddAllToCart(state);

    const productIdsNotAlreadySelected = allProductIds.filter(
      (id) => !state.selectedProducts[id] && availableProductIdsForAddAllToCart.includes(id)
    );

    let selectedAppIds: string[] = [];

    productIdsNotAlreadySelected.forEach((id) => {
      const product = state.products[id];
      const appIdentifier = product.containerId ?? id;

      const linkedApps = state.linkedEntitlements[appIdentifier] || [];
      const linkedAppIds = linkedApps.map(({ id }) => id);

      selectedAppIds = selectedAppIds.concat(linkedAppIds);
    });

    const selectedAppIdSet = new Set<string>();

    selectedAppIds.forEach((appId) => {
      if (!state.selectedLinkedEntitlements?.[appId]) {
        selectedAppIdSet.add(appId);
      }
    });

    const selectedProducts: State['selectedProducts'] = {};
    productIdsNotAlreadySelected.forEach((id) => {
      selectedProducts[id] = {};
    });

    const selectedLinkedEntitlements: State['selectedLinkedEntitlements'] = {};
    Array.from(selectedAppIdSet).forEach((id) => {
      selectedLinkedEntitlements[id] = {};
    });

    setState({
      ...state,
      selectedProducts: {
        ...state.selectedProducts,
        ...selectedProducts,
      },
      selectedLinkedEntitlements: {
        ...state.selectedLinkedEntitlements,
        ...selectedLinkedEntitlements,
      },
    });

    dispatch(actions.markBillEstimatesStale(allProductIds));
  },

  toggleSelectedItem: (
    identifier: string,
    type: SelectType,
    isToMakeDefaultBillingProfileSelection?: boolean,
    selectCheckedItems = false
  ): Action<State> => ({ setState, getState, dispatch }) => {
    const state = getState();

    const showSandboxProducts =
      state.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS] || false;
    const isSandboxDecouplingEnabled = state.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_DECOUPLING] || false;
    const isNonCommercialPricingEnabled =
      state.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING] || false;

    const isTWCPhase2Enabled = state.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2] || false;

    if (type === SelectType.SITE) {
      const allProducts = Object.values(state.products);
      const productsGroupedByCloudSiteOrOrgName = groupBy(allProducts, groupByCloudSiteOrOrgName);
      const orgProductInstancesMap = getOrgProductInstancesMap(state);
      const productsGroupedByCloudSiteOrOrgNameWithoutOrgInstances = productsGroupedByCloudSiteOrOrgName[
        identifier
      ].filter(
        (product) =>
          !(product.parentEntitlementId && Object.keys(orgProductInstancesMap).includes(product.parentEntitlementId))
      );
      const productsInCloudSiteOrOrg = isTWCPhase2Enabled
        ? productsGroupedByCloudSiteOrOrgNameWithoutOrgInstances
        : productsGroupedByCloudSiteOrOrgName[identifier];

      const isCloudSiteOrOrgNameSelected = isCloudSiteOrOrganisationSelected(state, identifier);
      const productIdsInCloudSiteOrOrg = productsInCloudSiteOrOrg.map((product) => product.id);
      const containerOrProductIds = productsInCloudSiteOrOrg.map((product) => product.containerId ?? product.id);
      // Get apps ids from selected product container, for orgs apps does not exists
      const appIdsInCloudSite = getAppsIdsForSelectedContainer({ ...state.linkedEntitlements }, containerOrProductIds);

      const selectedLinkedEntitlements = { ...state.selectedLinkedEntitlements };
      const selectedProducts = { ...state.selectedProducts };
      /**
       * As cloudsite selection behaves like a `Select ALL` for the group (products belonging to the cloudsite),
       * To uncheck a cloudsite means unchecking of all products under it.
       * Conversely, checking it means checking of all products under it.
       */
      if (isCloudSiteOrOrgNameSelected && !selectCheckedItems) {
        // Removing selected products and new products with apps when cloud site is uncheck
        const removedProductsAndAppsIds = getRemovedProductsAndAppsFromStore({
          products: { ...state.products },
          linkedEntitlements: { ...state.linkedEntitlements },
          selectedProducts,
          productIdsToRemove: productIdsInCloudSiteOrOrg,
          selectedLinkedEntitlements,
          appIdsToRemove: appIdsInCloudSite,
          showSandboxProducts,
          isSandboxDecouplingEnabled,
        });

        setState({
          ...state,
          ...removedProductsAndAppsIds,
        });
        dispatch(actions.markBillEstimatesStale([...productIdsInCloudSiteOrOrg, ...appIdsInCloudSite]));
        dispatch(
          actions.clearEstimatedUsersForRecurringBillEstimate([...productIdsInCloudSiteOrOrg, ...appIdsInCloudSite])
        );
        // on removal of cloud site, clear next bill date for all products and apps under it
        dispatch(actions.clearNextBillDateForEntitlements([...productIdsInCloudSiteOrOrg, ...appIdsInCloudSite]));
      } else {
        productIdsInCloudSiteOrOrg.forEach((id) => {
          if (showSandboxProducts && !isSandboxDecouplingEnabled) {
            /**
             * If any sandbox product present for product, add in selectedProducts.
             * If any parent to sandbox product present for product, add in selectedProducts.
             */

            const productIdOrContainerId = state.products[id]?.containerId ?? id;

            const allSandboxProductIdsInCart = getSandboxProductIdsInCart(state.products, productIdOrContainerId);
            allSandboxProductIdsInCart.forEach((sandboxProductId) => {
              selectedProducts[sandboxProductId] = {};
            });
            // If product is a sandbox product, add its parent product in selectedProducts
            const sandboxParentEntOrContainerId = state.products[id]?.sandboxParent;
            if (sandboxParentEntOrContainerId) {
              const sandboxParentProductId = getParentIdForSandboxProductInCart(
                state.products,
                sandboxParentEntOrContainerId
              );
              sandboxParentProductId.forEach((sandboxParentId) => {
                selectedProducts[sandboxParentId] = {};
              });
            }
          }
          if (!selectedProducts[id]) {
            selectedProducts[id] = {};
          }
        });
        appIdsInCloudSite.forEach((id) => {
          if (!selectedLinkedEntitlements[id]) {
            selectedLinkedEntitlements[id] = {};
          }
        });
        setState({
          ...state,
          selectedProducts: selectedProducts,
          selectedLinkedEntitlements: selectedLinkedEntitlements,
        });
      }
    }

    if (type === SelectType.ENTITLEMENT) {
      const selectedProducts = { ...state.selectedProducts };
      const selectedLinkedEntitlements = { ...state.selectedLinkedEntitlements };
      const products = { ...state.products };
      const linkedEntitlements = { ...state.linkedEntitlements };
      const isProductAlreadySelected = !!selectedProducts[identifier];
      const isAppAlreadySelected = !!selectedLinkedEntitlements[identifier];
      const product = products[identifier];
      const selectedAppData = Object.values(linkedEntitlements)
        ?.flatMap((item) => item)
        ?.find((app) => app.id === identifier);

      const currentContainerId = product?.containerId ?? selectedAppData?.containerOrProductId;
      // if a product selected is having a family container id, find all same container product ids
      // this is valid only for transfer use-case, for other flows we allow single product selection
      const selectedProductIdSet = new Set<string>();
      if (product) selectedProductIdSet.add(identifier);

      const appIdentifier = currentContainerId ?? product?.id;

      /**
       * Below code is added to fix below issue:
       * Issue: For a family container, if we add a new app in the container,
       * on removing any product from cart in the same container,
       * the new app is also getting removed, even though other product from container are available.
       * Solution: We are checking if any other product from the same container is selected,
       * then we are not removing the new app from the cart.
       */
      const allProductsInContainer =
        Object.keys(selectedProducts)?.filter((id) => products[id].containerId === currentContainerId) ?? [];

      // Filtering the current product because that is going to be removed
      const otherProductsInCart = allProductsInContainer.filter((id) => id !== identifier);

      const selectedAppIdSet = new Set<string>();
      if (isAppAlreadySelected) {
        selectedAppIdSet.add(identifier);
      } else {
        linkedEntitlements[appIdentifier]?.forEach((app) => {
          if (!(app.operationType === OperationType.NEW && otherProductsInCart.length > 0)) {
            selectedAppIdSet.add(app.id);
          }
        });
      }

      if (currentContainerId && product?.operationType === OperationType.TRANSFER) {
        Object.values(products)
          .filter((product) => product.containerId === currentContainerId)
          .forEach((product) => selectedProductIdSet.add(product.id));
      }
      if ((isProductAlreadySelected || isAppAlreadySelected) && !selectCheckedItems) {
        const removedProductsAndAppsIds = getRemovedProductsAndAppsFromStore({
          products: { ...state.products },
          linkedEntitlements: { ...state.linkedEntitlements },
          selectedProducts,
          productIdsToRemove: Array.from(selectedProductIdSet),
          selectedLinkedEntitlements,
          appIdsToRemove: Array.from(selectedAppIdSet),
          selectedApp: selectedAppData,
          showSandboxProducts,
          isSandboxDecouplingEnabled,
        });
        setState({
          ...state,
          ...removedProductsAndAppsIds,
        });

        dispatch(actions.markBillEstimatesStale([...selectedProductIdSet, ...selectedAppIdSet]));
        dispatch(actions.clearEstimatedUsersForRecurringBillEstimate([...selectedProductIdSet, ...selectedAppIdSet]));
        // on removal of entitlement, clear next bill date for all products and apps under it
        dispatch(actions.clearNextBillDateForEntitlements([...selectedProductIdSet, ...selectedAppIdSet]));
      } else {
        Array.from(selectedProductIdSet).forEach((id) => {
          if (showSandboxProducts && !isSandboxDecouplingEnabled) {
            /**
             * If any sandbox product present for product, add in selectedProducts.
             * If any parent to sandbox product present for product, add in selectedProducts.
             */

            const productIdOrContainerId = products[id]?.containerId ?? id;
            const allSandboxProductIdsInCart = getSandboxProductIdsInCart(state.products, productIdOrContainerId);
            allSandboxProductIdsInCart.forEach((sandboxProductId) => {
              selectedProducts[sandboxProductId] = {};
            });
            // If product is a sandbox product, add its parent product in selectedProducts
            const sandboxParentEntOrContainerId = state.products[id]?.sandboxParent;
            if (sandboxParentEntOrContainerId) {
              const sandboxParentProductId = getParentIdForSandboxProductInCart(
                state.products,
                sandboxParentEntOrContainerId
              );
              sandboxParentProductId.forEach((sandboxParentId) => {
                selectedProducts[sandboxParentId] = {};
              });
            }
          }
          selectedProducts[id] = {};
        });
        Array.from(selectedAppIdSet).forEach((id) => {
          selectedLinkedEntitlements[id] = {};
        });
        setState({
          ...state,
          selectedProducts,
          selectedLinkedEntitlements,
        });
      }

      const cloudSiteName = state.products[identifier]?.cloudSite;
      const updatedState = getState();
      const selectedProductsData = getSelectedProducts(updatedState);
      const productsGroupedByCloudSites = groupBy(selectedProductsData, 'cloudSite');
      const hasSelectedProductsUnderCloudSite = productsGroupedByCloudSites[cloudSiteName]?.length > 0;

      if (hasSelectedProductsUnderCloudSite) {
        const selectedNextBillDate = updatedState.nextBillDate.cloudSiteLevel?.[cloudSiteName];
        dispatch(actions.setNextBillDateInSelectedProducts(cloudSiteName, selectedNextBillDate));
      } else {
        dispatch(actions.clearNextBillDate(cloudSiteName));
      }

      if (updatedState.nextBillDate?.entitlementLevel) {
        dispatch(actions.clearNextBillDateForEntitlements(Array.from(selectedProductIdSet)));
      }
    }

    // Stopping default IG logic when Non commercial pricing is enabled
    if (isToMakeDefaultBillingProfileSelection && !isNonCommercialPricingEnabled) {
      dispatch(actions.setDefaultBillingProfile());
    }

    dispatch(actions.markBillEstimatesStale());
  },

  toggleCheckedItem: (identifiers: string[]): Action<State> => ({ dispatch }) => {
    if (identifiers.length > 0) {
      identifiers.forEach((identifier) => {
        dispatch(actions.toggleSelectedItem(identifier, SelectType.ENTITLEMENT, true));
      });
    }
  },

  setDefaultBillingProfile: (): Action<State> => ({ getState, dispatch }) => {
    const state = getState();
    const defaultBillingProfile = getDefaultBillingProfile({ state });
    dispatch(actions.setInvoiceGroup(defaultBillingProfile));
  },

  removeCloudSiteFromCart: (
    cloudSiteName: string,
    isToMakeDefaultBillingProfileSelection?: boolean
  ): Action<State> => ({ setState, getState, dispatch }) => {
    const state = getState();
    const allApps = Object.values(state.linkedEntitlements);
    const selectedLinkedEntitlements = { ...state.selectedLinkedEntitlements };
    const selectedProducts = { ...state.selectedProducts };
    const selectedProductIds = Object.keys(selectedProducts);
    const stitchedSelectedProducts = stitchChangeKeyObjects(state.products, selectedProducts);
    const productIdsToRemove = selectedProductIds.filter(
      (selectedProductId) => stitchedSelectedProducts[selectedProductId].cloudSite === cloudSiteName
    );

    const isNonCommercialPricingEnabled =
      state.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING] || false;

    const selectedContainerIdOrProductId = productIdsToRemove.map(
      (selectedProductId) =>
        stitchedSelectedProducts[selectedProductId].containerId ?? stitchedSelectedProducts[selectedProductId].id
    );

    // Get apps ids from selected product container
    const appIdsInCloudSite = getAppsIdsForSelectedContainer(
      { ...state.linkedEntitlements },
      selectedContainerIdOrProductId
    );

    const removedProductsAndAppsIds = getRemovedProductsAndAppsFromStore({
      products: { ...state.products },
      linkedEntitlements: { ...state.linkedEntitlements },
      selectedProducts,
      productIdsToRemove,
      selectedLinkedEntitlements,
      appIdsToRemove: appIdsInCloudSite,
    });

    setState({
      ...state,
      ...removedProductsAndAppsIds,
    });

    // Stopping default IG logic when Non commercial pricing is enabled
    if (isToMakeDefaultBillingProfileSelection && !isNonCommercialPricingEnabled) {
      dispatch(actions.setDefaultBillingProfile());
    }
    dispatch(actions.markBillEstimatesStale([...productIdsToRemove, ...appIdsInCloudSite]));
    dispatch(actions.clearEstimatedUsersForRecurringBillEstimate([...productIdsToRemove, ...appIdsInCloudSite]));
    dispatch(actions.clearNextBillDate(cloudSiteName));
  },

  setSelectedPricingPlanId: (
    productId: CartProductSchema['id'],
    updatedPricingPlanId: string,
    updatedOfferingId: string
  ): Action<State> => async ({ setState, getState, dispatch }) => {
    const state = getState();

    const isTWCPhase2Enabled = state.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2] || false;

    let updatedSelectedInstancesObj = {};
    if (isTWCPhase2Enabled) {
      // Get all the instances of the org product
      const orgProductInstancesMap = getOrgProductInstancesMap(state);
      const orgProductInstances = orgProductInstancesMap[productId];

      /**
       * When Collection product plan is updated, get Map of corresponding plan for each instance.
       * This is to update the instance with new offering ID and name.
       */
      const collectionInstanceProductIdOfferingIdMap = await getCollectionInstanceProductIdOfferingIdMap({
        collectionToInstanceOfferingMap: state.collectionToInstanceOfferingMap,
        requestOfferingId: updatedOfferingId,
        orgProductInstances: orgProductInstances ?? [],
      });

      // Below is to get updated selected instances with changed offering ID and offering name.
      const updatedSelectedInstancesOffering = getUpdatedSelectedInstancesOffering({
        initialProducts: state.products,
        selectedProducts:
          orgProductInstances?.reduce((acc: Record<string, CartProductSchema>, instance) => {
            acc[instance.id] = instance;
            return acc;
          }, {}) ?? {},
        collectionInstanceProductIdOfferingIdMap,
        offeringsAndpricingPlans: state?.offeringsAndpricingPlans,
      });

      updatedSelectedInstancesOffering.forEach((instance) => {
        updatedSelectedInstancesObj = { ...updatedSelectedInstancesObj, ...instance };
      });
    }

    let trialLengthInDays: string | undefined | null;
    const offeringDetails = state?.offeringsAndpricingPlans?.[state.products[productId]?.productId]?.find(
      (offering) => offering?.id === updatedOfferingId
    );

    if (isNewEntitlement(state.products[productId]) && state?.offeringsAndpricingPlans) {
      trialLengthInDays = offeringDetails?.trialLengthInDays;
    }

    const updatedSelectedProducts = getSelectedProductsAppsObj({
      initialProducts: state.products,
      selectedProducts: state.selectedProducts,
      identifier: productId,
      changeObj: {
        pricingPlanId: updatedPricingPlanId,
        offeringId: updatedOfferingId,
        ...(trialLengthInDays && { trialLengthInDays }),
      },
    });

    const updatedAddons = getUpdatedAddons({
      product: state.products[productId],
      linkedEntitlements: state.linkedEntitlements,
      selectedLinkedEntitlements: state.selectedLinkedEntitlements,
      updatedOfferingId,
      updatedOfferingName: offeringDetails?.offeringName,
      eligibleStatuspageAddons: state.statuspageAddonsOfferingsMap?.[updatedOfferingId],
    });

    const selectedAppsDetails = getSelectedAppsDetails({
      linkedEntitlements: state.linkedEntitlements,
      selectedLinkedEntitlements: { ...state.selectedLinkedEntitlements, ...updatedAddons.selectedLinkedEntitlements },
      identifier: productId,
      changeObj: {
        pricingPlanId: updatedPricingPlanId,
        offeringId: updatedOfferingId,
      },
      offeringsAndpricingPlans: state?.offeringsAndpricingPlans,
    });

    const updatedState: State = {
      ...state,
      selectedProducts: { ...updatedSelectedProducts, ...updatedSelectedInstancesObj },
      linkedEntitlements: updatedAddons.linkedEntitlements,
      selectedLinkedEntitlements: {
        ...updatedAddons.selectedLinkedEntitlements,
        ...selectedAppsDetails,
      },
    };
    setState(updatedState);
    /**
     * In case it is new entitlement and bill date for this new entitlement is already changed,
     * on updating the plan for such entitlement, we need to update the next bill date,
     * based on trial period for the new selected plan.
     *
     * In order to achieve this, dispatching the action setNextBillAnchorDate.
     */
    if (isNewEntitlement(state.products[productId]) && state.nextBillDate?.entitlementLevel?.[productId]) {
      //Fetching selected nextBillDate for the entitlement.
      const changedNextBillDateString = state.nextBillDate.entitlementLevel[productId];
      const changedNextBillDate = new Date(Number(changedNextBillDateString));
      const selectedDate = changedNextBillDate.getDate();

      //Creating timestamp with selected date, same way as done via changeBillDate timestamp.
      const selectedNextBillDate = { label: selectedDate, value: selectedDate };

      dispatch(
        actions.setNextBillAnchorDate(
          { [productId]: SelectType.ENTITLEMENT },
          getTimestampFromDate(selectedNextBillDate)
        )
      );
    }
    dispatch(actions.markBillEstimatesStale([productId]));
  },
  setActiveUsers: (productId: CartProductSchema['id'], activeUsers: string): Action<State> => ({
    setState,
    getState,
    dispatch,
  }) => {
    const state = getState();
    const estimatedUsersForRecurringBillEstimate = {
      ...state.estimatedUsersForRecurringBillEstimate,
      [productId]: activeUsers,
    };
    const updatedState = {
      ...state,
      estimatedUsersForRecurringBillEstimate,
    };
    const containerOrProductId = state.products[productId]?.containerId || state.products[productId]?.id;
    //container id or line item id will not be present in store.products when we pass productId of linked entitlement
    if (containerOrProductId) {
      setState(updatedState);
      dispatch(actions.setAppsEstimatedUsersForContainerOrProductId([containerOrProductId]));
    } else {
      // we are allowing addons to edit active users from the cart
      const linkedEntitlement = flatMap(Object.values(state.linkedEntitlements))?.find(
        (linkedEntitlement) => linkedEntitlement?.id === productId
      );
      if (linkedEntitlement) estimatedUsersForRecurringBillEstimate[linkedEntitlement.id] = activeUsers;
      setState(updatedState);
    }
    dispatch(actions.markBillEstimatesStale([productId]));
  },

  clearEstimatedUsersForRecurringBillEstimate: (entitlements: string[]): Action<State> => ({ setState, getState }) => {
    if (!entitlements) {
      return;
    }
    const state = getState();

    const estimatedUsersForRecurringBillEstimate = omit(state.estimatedUsersForRecurringBillEstimate, entitlements);

    setState({
      ...state,
      estimatedUsersForRecurringBillEstimate,
    });
  },
  setAppsEstimatedUsersForContainerOrProductId: (containerOrProductIds?: string[]): Action<State> => ({
    setState,
    getState,
  }) => {
    const state = getState();
    const allApps = { ...state.linkedEntitlements };
    const estimatedUsersForApps: { [id: CartLinkedEntitlementsSchema['id']]: string } = {};
    /** Update active user count for apps in family container, If `containerId` is not available for the product then we are considering `id` of the product because we are grouping non jira apps with `id`
     * when product in new site is updated with max user count */
    containerOrProductIds?.forEach((containerOrProductId) => {
      // Get max user count for the product or container and update the active user count for all apps in the container
      const maxUser = getActiveUserCountForContainerOrProductId({
        products: state.products,
        selectedProducts: state.selectedProducts,
        containerOrProductId: containerOrProductId,
        newActiveUserCounts: [],
        estimatedUsersForRecurringBillEstimate: state.estimatedUsersForRecurringBillEstimate,
      });

      // Get all apps associated with the container or product
      const appListAssociatedWithContainer = allApps[containerOrProductId];
      appListAssociatedWithContainer?.forEach((appAssociatedWithContainer) => {
        const isAddOn = appAssociatedWithContainer?.relationshipType === LinkedEntitlementRelationShipType.ADD_ON;
        // Update the recurring estimate for the linked entitlement which is not having the add-on
        if (!isAddOn && maxUser && maxUser > 0) {
          // Update the recurring estimate for the app with the max user count
          estimatedUsersForApps[appAssociatedWithContainer.id] = String(maxUser);
        }
      });
    });

    setState({
      ...state,
      estimatedUsersForRecurringBillEstimate: {
        ...state.estimatedUsersForRecurringBillEstimate,
        ...estimatedUsersForApps,
      },
    });
  },
  setSelectedPartnerAccountData: ({
    discountTier,
    programLevelGlobal,
    transactionAccountId,
    name,
    taxId,
    billTo,
    partnerType,
  }: {
    transactionAccountId: string;
    discountTier: string;
    programLevelGlobal: string;
    name: string;
    taxId: string;
    billTo: BillToInfo;
    partnerType: string;
  }): Action<State> => ({ setState, getState }) => {
    setState({
      ...getState(),
      selectedPartnerAccountData: {
        discountTier,
        programLevelGlobal,
        transactionAccountId,
        name,
        taxId,
        billTo,
        partnerType,
      },
    });
  },

  getNativeProductsWithOfferingsAndPricingPlans: ({
    filterOptions,
    includeExperimentalProducts = false,
    isMultiCurrencyEnabled = false,
    isAddOnsSearchEnabled = false,
    isStatuspageEnabled = false,
    isTeamWorkCollectionEnabled = false,
    isStrategyCollectionEnabled = false,
  }: {
    filterOptions?: [FilterOption];
    includeExperimentalProducts?: boolean;
    isMultiCurrencyEnabled?: boolean;
    isAddOnsSearchEnabled?: boolean;
    isStatuspageEnabled?: boolean;
    isTeamWorkCollectionEnabled?: boolean;
    isStrategyCollectionEnabled?: boolean;
  }): Action<State> => async ({ setState, getState }) => {
    const state = getState();
    setState({
      ...state,
      isLoading: true,
    });
    try {
      const productsWithOfferingsAndPricingPlans = await getNativeProductsWithOfferingsAndPricingPlans({
        filterOptions,
        includeExperimentalProducts,
        isMultiCurrencyEnabled,
        isAddOnsSearchEnabled,
        isStatuspageEnabled,
        isTeamWorkCollectionEnabled,
        isStrategyCollectionEnabled,
      });
      const state = getState();
      setState({
        ...state,
        isLoading: false,
        hasNativeProductWithPricingPlanFetchFailed: false,
        ...productsWithOfferingsAndPricingPlans,
      });
    } catch (err) {
      const state = getState();
      setState({
        ...state,
        hasNativeProductWithPricingPlanFetchFailed: true,
        isLoading: false,
      });
    }
  },
  updateCollectionToInstanceOfferingMap: (
    collectionToInstanceOfferingMapResponse: CollectionToInstanceOfferingMapResponse
  ): Action<State> => async ({ setState, getState }) => {
    const state = getState();
    setState({
      ...state,
      collectionToInstanceOfferingMap: collectionToInstanceOfferingMapResponse,
    });
  },

  getBillEstimate: (): Action<State> => async ({ setState, getState }) => {
    const state = getState();
    const isMultiCurrencyEnabled = state?.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES] ?? false;
    const isNonCommercialPricingEnabled = state?.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING];
    const isExcludeExpiredPromotionEnabled =
      state?.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_EXCLUDE_EXPIRED_PROMOTIONS_IN_UOF] ?? false;
    const isAddOnsSearchEnabled = state?.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_ADD_ONS_PHASE_V2] ?? false;
    const isAppEditionSupportEnabled = state?.featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_APP_EDITION_SUPPORT] ?? false;

    const selectedProducts = Object.values(
      stitchChangeKeyObjects<CartProductSchema>(state.products, state.selectedProducts)
    );

    const selectedLinkedEntitlementList = getSelectedAppsData(state);

    if (!state.selectedPartnerAccountData) return;

    const isCartEmpty = selectedProducts.length === 0;

    // Do not trigger estimate fetch if the cart is empty
    if (isCartEmpty) {
      if (state.billEstimate) {
        setState({
          ...state,
          billEstimate: null,
        });
      }

      if (state.recurringBillEstimate) {
        setState({
          ...state,
          recurringBillEstimate: null,
        });
      }

      return;
    }

    setState({
      ...state,
      isLoading: true,
      isRefreshEstimateLoading: true,
    });

    try {
      const {
        linkedEntitlements: sandboxLinkedApps,
        products: sandboxLinkedProducts,
      } = getSandboxEntitlementsLinkedToProducts(selectedProducts, {
        sandboxProductsObj: state.sandboxProducts,
        sandboxAppsObj: state.sandboxApps,
      });
      const partnerPromotionsPromise = getPartnerPromotions({
        existingPromotions: state.partnerPromotions,
        existingPromotionBenefits: state.partnerPromotionBenefits,
        selectedPartnerAccountData: state.selectedPartnerAccountData,
        selectedProducts: [...selectedProducts, ...sandboxLinkedProducts],
        selectedLinkedEntitlements: [...(selectedLinkedEntitlementList ?? []), ...sandboxLinkedApps],
        isExpandPromotionBenefits: true,
      });
      const allCartEntitlementProductIds = [...(selectedProducts ?? []), ...(selectedLinkedEntitlementList ?? [])].map(
        (entitlement) => entitlement.productId
      );
      const offeringsAndPricingPlansPromise = getProductIdsOfferingId({
        existingOfferingsAndPricingPlans: state.offeringsAndpricingPlans,
        productIds: allCartEntitlementProductIds,
        isMultiCurrencyEnabled,
        isAddOnsSearchEnabled,
      });
      const [
        { productIdPartnerPromotionIdMap, promotionIdToBenefitsMap },
        offeringsAndpricingPlans,
      ] = await Promise.all([partnerPromotionsPromise, offeringsAndPricingPlansPromise]);

      setState({
        ...getState(),
        partnerPromotions: {
          ...(state.partnerPromotions || {}),
          ...productIdPartnerPromotionIdMap,
        },
        partnerPromotionBenefits: {
          ...(state.partnerPromotionBenefits || {}),
          ...promotionIdToBenefitsMap,
        },
        offeringsAndpricingPlans: offeringsAndpricingPlans,
      });

      const {
        partnerPromotions,
        linkedEntitlements,
        nextBillDate,
        products,
        selectedLinkedEntitlements,
        partnerPromotionBenefits,
        billingProfiles,
      } = getState();

      const currentBillingProfile = billingProfiles?.find((profile) => profile.id === state.invoiceGroupId);
      const currentPricingEligibility = currentBillingProfile?.shipToPriceEligibility ?? PricingPlanType.COMMERCIAL;
      const currentCurrency = currentBillingProfile?.currency ?? Currency.USD;

      const recurringEstimateRequest: Parameters<typeof getBillEstimate>[0] = {
        cartProducts: selectedProducts,
        cartApps: linkedEntitlements,
        transactionAccountId: state.selectedPartnerAccountData.transactionAccountId,
        partnerPromotions: partnerPromotions,
        invoiceGroupId: state.invoiceGroupId,
        nextBillDate,
        estimateType: BillEstimateType.RECURRING_ESTIMATE,
        selectedProducts: state.selectedProducts,
        products: products,
        selectedLinkedEntitlements,
        offeringsAndpricingPlans: offeringsAndpricingPlans,
        estimatedUsersForRecurringBillEstimate: state.estimatedUsersForRecurringBillEstimate,
        shouldExcludeDiscretionaryDiscount: true,
        partnerPromotionBenefits,
        currentPricingEligibility,
        currentCurrency,
        includeSubscriptionStartTime: true,
        isNonCommercialPricingEnabled,
        isExcludeExpiredPromotionEnabled,
        isAppEditionSupportEnabled,
      };

      const recurringEstimatePromise = getBillEstimate(recurringEstimateRequest);
      const nextEstimatePromise = getBillEstimate({
        ...recurringEstimateRequest,
        estimateType: BillEstimateType.NEXT_ESTIMATE,
      });

      // const billEstimates = await Promise.all([recurringEstimatePromise, nextEstimatePromise]);
      // const [recurringEstimate, nextEstimate] = billEstimates;

      const recurringEstimate = await recurringEstimatePromise;

      let nextEstimate;
      try {
        nextEstimate = await nextEstimatePromise;
      } catch (e) {
        // silently fail next estimate fetch
      }

      const selectedCartApps: CartLinkedEntitlementsSchema[] = [];
      const productOrContainerIds = uniq(selectedProducts?.map(({ id, containerId }) => containerId || id));
      productOrContainerIds?.forEach((productOrContainerId) => {
        linkedEntitlements[productOrContainerId]?.forEach((app) => selectedCartApps.push(app));
      });

      const processedRecurringEstimateResponse = processBillEstimateResponse({
        billEstimateResponse: recurringEstimate,
        cartProducts: selectedProducts,
        cartApps: selectedCartApps,
      });

      let processedBillEstimateResponse;
      if (nextEstimate) {
        processedBillEstimateResponse = processBillEstimateResponse({
          billEstimateResponse: nextEstimate,
          cartProducts: selectedProducts,
          cartApps: selectedCartApps,
        });
      }

      setState({
        ...getState(),
        isLoading: false,
        isRefreshEstimateLoading: false,
        hasProductMetadataFetchFailed: false,
        billEstimate: processedBillEstimateResponse,
        recurringBillEstimate: processedRecurringEstimateResponse,
      });
    } catch (e) {
      setState({
        isLoading: false,
        isRefreshEstimateLoading: false,
        hasProductMetadataFetchFailed: true,
      });
    }
  },

  setValidationErrors: (validationErrors: ValidationErrorSchema[]): Action<State> => ({ setState, getState }) => {
    if (!validationErrors) return;

    const cartValidationErrors = keyBy(validationErrors, 'entitlementId');

    setState({
      ...getState(),
      validationErrors: cartValidationErrors,
    });
  },

  clearNextBillDateForEntitlements: (entitlements?: string[]): Action<State> => ({ setState, getState }) => {
    if (!entitlements) {
      return;
    }
    const state = getState();

    const otherEntitlementBillDates = omit(state.nextBillDate.entitlementLevel, entitlements);

    setState({
      ...state,
      nextBillDate: {
        ...state.nextBillDate,
        entitlementLevel: otherEntitlementBillDates,
      },
    });
  },

  setNextBillDateForEntitlements: (
    entitlements?: Record<string, SelectType>,
    nextBillDate?: string,
    linkedProducts?: Record<string, CartProductSchema>
  ): Action<State> => ({ setState, getState }) => {
    const state = getState();
    const stitchedProducts = stitchChangeKeyObjects<CartProductSchema>(state.products, state.selectedProducts);
    const { products, selectedProducts, selectedLinkedEntitlements } = state;
    const updatedSelectedProducts: Record<string, Partial<CartProductSchema>> = {};
    const updatedSelectedApps: Record<string, Partial<CartLinkedEntitlementsSchema>> = {};

    if (!entitlements || nextBillDate === '') {
      return;
    }

    for (const [identifier, product] of Object.entries(products)) {
      const selectedProduct = selectedProducts[identifier];
      const entitlement = entitlements[identifier];

      const productNextBillDate = isNewEntitlement(product)
        ? getNextBillDateForNewEntitlements(stitchedProducts[identifier]?.trialLengthInDays)
        : product.nextBillDate;
      const validNextBillDateTimeStamp = getValidNextBillDateTimeStamp(productNextBillDate, nextBillDate);
      if (selectedProduct && entitlement === SelectType.ENTITLEMENT) {
        const updatedProducts = getSelectedProductsAppsObj({
          initialProducts: state.products,
          selectedProducts: state.selectedProducts,
          identifier,
          changeObj: {
            nextBillDate: validNextBillDateTimeStamp,
          },
        });
        updatedSelectedProducts[identifier] = updatedProducts[identifier];
      } else if (linkedProducts && linkedProducts[identifier]) {
        const updatedProducts = getSelectedProductsAppsObj({
          initialProducts: state.products,
          selectedProducts: state.selectedProducts,
          identifier,
          changeObj: {
            nextBillDate: validNextBillDateTimeStamp,
          },
        });
        updatedSelectedProducts[identifier] = updatedProducts[identifier];
      }
    }

    const stitchedAppsObj = getSelectedAppsObj(state) ?? {};
    for (const [identifier, app] of Object.entries(stitchedAppsObj)) {
      const selectedApp = selectedLinkedEntitlements?.[identifier];
      const productContainerId = app?.containerOrProductId ?? '';
      // In case of App is part of Jira Family container the containerId won't be directly mapped in store products
      // the 'entitlements' can have a single linked product, so we need to match with any of the linked products related to app
      const linkedProductsToApp = products?.[productContainerId]
        ? [products?.[productContainerId]]
        : Object.values(products).filter((product) => product.containerId === productContainerId);
      if (selectedApp && linkedProductsToApp.some((linkedProduct) => !!entitlements[linkedProduct?.id])) {
        const appNextBillDate = isNewEntitlement(app)
          ? getNextBillDateForNewEntitlements(app?.trialLengthInDays)
          : app.nextBillDate;
        const validNextBillDateTimeStamp = getValidNextBillDateTimeStamp(
          appNextBillDate ? appNextBillDate : '',
          nextBillDate
        );
        const updatedApps = getSelectedProductsAppsObj({
          initialProducts: stitchedAppsObj,
          selectedProducts: selectedLinkedEntitlements,
          identifier,
          changeObj: {
            nextBillDate: validNextBillDateTimeStamp,
          },
          ignoreKeys: ['productStatuses'],
        });
        // this is common method for Apps and products so need to typecast for apps here
        updatedSelectedApps[identifier] = updatedApps[identifier] as Partial<CartLinkedEntitlementsSchema>;
      }
    }

    setState({
      ...state,
      selectedProducts: { ...selectedProducts, ...updatedSelectedProducts },
      selectedLinkedEntitlements: { ...selectedLinkedEntitlements, ...updatedSelectedApps },
    });
  },

  setNextBillAnchorDate: (
    entitlements: Record<string, SelectType>,
    nextBillDateTimestamp: string,
    linkedProducts?: Record<string, CartProductSchema>
  ): Action<State> => ({ setState, getState, dispatch }) => {
    const state = getState();
    const selectedProducts = stitchChangeKeyObjects<CartProductSchema>(state.products, state.selectedProducts);
    const entitlementsList = Object.keys(entitlements).filter(
      (identifier) => entitlements[identifier] !== SelectType.SITE
    );
    let entitlementLevelNextBillDate: Record<string, string> = {};
    entitlementsList?.map((entitlement) => {
      /**
       * For new entitlements, next bill date is calculated based on the trial length
       * And validNextBillDateTimeStamp is calculated based on the next bill date calculated in first step
       */
      const productNextBillDate = isNewEntitlement(state.products[entitlement])
        ? getNextBillDateForNewEntitlements(selectedProducts[entitlement]?.trialLengthInDays)
        : state.products[entitlement]?.nextBillDate;
      const validNextBillDateTimeStamp = getValidNextBillDateTimeStamp(productNextBillDate, nextBillDateTimestamp);
      entitlementLevelNextBillDate = { ...entitlementLevelNextBillDate, [entitlement]: validNextBillDateTimeStamp };

      const productContainerId = state.products[entitlement]?.containerId ?? state.products[entitlement]?.id;
      const stichedAppsObj = getSelectedAppsObj(state) ?? {};
      if (stichedAppsObj) {
        for (const [identifier, app] of Object.entries(stichedAppsObj)) {
          if (app.containerOrProductId === productContainerId) {
            const appNextBillDate = isNewEntitlement(app)
              ? getNextBillDateForNewEntitlements(app?.trialLengthInDays)
              : app.nextBillDate;
            const validNextBillDateTimeStamp = getValidNextBillDateTimeStamp(
              appNextBillDate ? appNextBillDate : '',
              nextBillDateTimestamp
            );
            entitlementLevelNextBillDate = {
              ...entitlementLevelNextBillDate,
              [identifier]: validNextBillDateTimeStamp,
            };
          }
        }
      }
    });

    if (linkedProducts) {
      const linkedEntitlementsList = Object.keys(linkedProducts);
      if (linkedEntitlementsList?.length > 0) {
        linkedEntitlementsList?.forEach((entitlement) => {
          const linkedProductNextBillDate = isNewEntitlement(state.products[entitlement])
            ? getNextBillDateForNewEntitlements(state.products[entitlement]?.trialLengthInDays)
            : state.products[entitlement]?.nextBillDate;
          const validNextBillDateTimeStamp = getValidNextBillDateTimeStamp(
            linkedProductNextBillDate,
            nextBillDateTimestamp
          );
          entitlementLevelNextBillDate = { ...entitlementLevelNextBillDate, [entitlement]: validNextBillDateTimeStamp };
        });
      }
    }

    setState({
      ...state,
      nextBillDate: {
        ...state.nextBillDate,
        entitlementLevel: {
          ...(state.nextBillDate.entitlementLevel ?? {}),
          ...entitlementLevelNextBillDate,
        },
      },
    });
    dispatch(actions.setNextBillDateForEntitlements(entitlements, nextBillDateTimestamp, linkedProducts));
    // due to phase promotions sending discrete promotions, reset the estimations as well
    dispatch(actions.markBillEstimatesStale(Object.keys(entitlementLevelNextBillDate)));
  },

  setNextBillDateInSelectedProducts: (cloudSiteHostName?: string, nextBillDate?: string): Action<State> => ({
    setState,
    getState,
  }) => {
    const state = getState();
    const { products, selectedProducts, selectedLinkedEntitlements } = state;
    const updatedSelectedProducts: Record<string, Partial<CartProductSchema>> = {};
    const updatedSelectedApps: Record<string, Partial<CartLinkedEntitlementsSchema>> = {};

    if (cloudSiteHostName) {
      for (const [identifier, product] of Object.entries(products)) {
        const selectedProduct = selectedProducts[identifier];
        if (selectedProduct && product.cloudSite === cloudSiteHostName) {
          const updatedProducts = getSelectedProductsAppsObj({
            initialProducts: state.products,
            selectedProducts: state.selectedProducts,
            identifier,
            changeObj: {
              nextBillDate: nextBillDate || product.nextBillDate,
            },
          });
          updatedSelectedProducts[identifier] = updatedProducts[identifier];
        }
      }
      const stichedAppsObj = getSelectedAppsObj(state) ?? {};
      for (const [identifier, app] of Object.entries(stichedAppsObj)) {
        const selectedApp = selectedLinkedEntitlements?.[identifier];
        const productContainerId = app?.containerOrProductId ?? '';
        // In case of App is part of Jira Family container the containerId won't be directly mapped in store products
        const linkedProduct =
          products?.[productContainerId] ??
          Object.values(products).find((product) => product.containerId === productContainerId && product.cloudSite);
        if (selectedApp && linkedProduct?.cloudSite === cloudSiteHostName) {
          const updatedApps = getSelectedProductsAppsObj({
            initialProducts: stichedAppsObj,
            selectedProducts: selectedLinkedEntitlements,
            identifier,
            changeObj: {
              nextBillDate: nextBillDate || app.nextBillDate,
            },
            ignoreKeys: ['productStatuses'],
          });
          // this is common method for Apps and products so need to typecast for apps here
          updatedSelectedApps[identifier] = updatedApps[identifier] as Partial<CartLinkedEntitlementsSchema>;
        }
      }
    }

    setState({
      ...state,
      selectedProducts: { ...selectedProducts, ...updatedSelectedProducts },
      selectedLinkedEntitlements: { ...selectedLinkedEntitlements, ...updatedSelectedApps },
    });
  },

  setNextBillDate: (nextBillDateTimestamp: string, cloudSiteHostName?: string): Action<State> => ({
    setState,
    getState,
    dispatch,
  }) => {
    const state = getState();

    if (!cloudSiteHostName) {
      setState({
        ...state,
        nextBillDate: {
          ...state.nextBillDate,
          cartLevel: nextBillDateTimestamp,
        },
      });
    } else {
      setState({
        ...state,
        nextBillDate: {
          ...state.nextBillDate,
          cloudSiteLevel: {
            ...(state.nextBillDate.cloudSiteLevel ?? {}),
            [cloudSiteHostName]: nextBillDateTimestamp,
          },
        },
      });
    }
    dispatch(actions.setNextBillDateInSelectedProducts(cloudSiteHostName, nextBillDateTimestamp));
  },

  clearNextBillDate: (cloudSiteHostName?: string): Action<State> => ({ setState, getState, dispatch }) => {
    const state = getState();

    if (!cloudSiteHostName) {
      setState({
        ...state,
        nextBillDate: {},
      });
    } else {
      const otherCloudsiteBillDates = omit(state.nextBillDate.cloudSiteLevel, cloudSiteHostName);

      setState({
        ...state,
        nextBillDate: {
          ...state.nextBillDate,
          cloudSiteLevel: otherCloudsiteBillDates,
        },
      });
    }
    dispatch(actions.setNextBillDateInSelectedProducts(cloudSiteHostName));
  },

  setSearchParams: ({
    searchQuery,
    statuses,
  }: {
    searchQuery?: string;
    statuses?: CartProductSchema['productStatuses'];
  }): Action<State> => ({ setState, getState }) => {
    const state = getState();

    const updatedState: State = {
      ...state,
      cartSearch: {
        query: searchQuery,
        statuses: statuses,
      },
    };

    setState(updatedState);
  },

  clearSearchQuery: (): Action<State> => ({ setState, getState }) => {
    const state = getState();
    setState({
      ...state,
      cartSearch: {
        ...state.cartSearch,
        query: '',
      },
    });
  },
  setFeatureFlag: (featureFlagObj: FeatureFlags): Action<State> => ({ setState, getState }) => {
    const state = getState();
    const featureFlags = state.featureFlags;

    const updatedState: State = {
      ...state,
      featureFlags: {
        ...featureFlags,
        ...featureFlagObj,
      },
    };

    setState(updatedState);
  },
  setBillingProfiles: (billingProfiles: NonNullable<State['billingProfiles']>): Action<State> => ({
    setState,
    getState,
  }) => {
    setState({
      ...getState(),
      billingProfiles,
    });
  },
  markBillEstimatesStale: (productIds: CartProductSchema['id'][] = []): Action<State> => ({ setState, getState }) => {
    /**
     * Stale bill estimations remove products level estimations and other derived values (like totals)
     * when the product offering and other states change.
     */
    const state = getState();

    // const productAndAppIdsToRemove = productIds?.reduce((allIds, productId) => {
    //   const linkedProductAndAppsIds = getIdsOfLinkedProductsAndApps({
    //     productId,
    //     productsObj: state.products,
    //     appsObj: state.apps,
    //   });
    //   allIds = allIds.concat(linkedProductAndAppsIds.productIds, linkedProductAndAppsIds.appIds);
    //   return allIds;
    // }, productIds);

    const recurringBillEstimate = Object.assign({}, state.recurringBillEstimate);

    if (recurringBillEstimate && recurringBillEstimate.estimates) {
      productIds?.forEach((productId) => {
        delete recurringBillEstimate.estimates?.[productId];
      });
    }

    setState({
      ...state,
      recurringBillEstimate: {
        ...state.recurringBillEstimate,
        estimates: recurringBillEstimate.estimates,
        partnerPricing: null,
        taxItemsMap: null,
        adminTaxDetailsMap: null,
      },
    });
  },
  updatePartnerPromotions: (
    partnerPromotions: State['partnerPromotions'],
    partnerIdToBenefitMap: State['partnerPromotionBenefits']
  ): Action<State> => async ({ setState, getState }) => {
    const state = getState();
    setState({
      partnerPromotions: {
        ...state.partnerPromotions,
        ...partnerPromotions,
      },
      partnerPromotionBenefits: {
        ...state.partnerPromotionBenefits,
        ...partnerIdToBenefitMap,
      },
    });
  },
  updateOfferingAndPricingPlans: (
    offeringsAndPricingPlans: State['offeringsAndpricingPlans']
  ): Action<State> => async ({ setState, getState }) => {
    const state = getState();
    setState({
      offeringsAndpricingPlans: {
        ...state.offeringsAndpricingPlans,
        ...offeringsAndPricingPlans,
      },
    });
  },
  updateSandboxParentData: (sandboxParentData: State['sandboxParentData']): Action<State> => async ({
    setState,
    getState,
  }) => {
    const state = getState();
    setState({
      sandboxParentData: {
        ...state.sandboxParentData,
        ...sandboxParentData,
      },
    });
  },
};

export type Actions = typeof actions;

export const Store = createStore<State, Actions>({
  initialState,
  actions,
  name: 'Unified Order Flow',
});

export const useStore = createHook(Store);

export const useStoreActions = createActionsHook(Store);

export const useHasProductEnrichmentFailed = createStateHook(Store, {
  selector: (state: State) => {
    const allEnrichmentStatuses = Object.values(state.enrichmentStatus ?? {});
    const firstErrorStatus = allEnrichmentStatuses.find((status) => status === EnrichmentStatus.ERROR);

    return !!firstErrorStatus;
  },
});

export const useIsProductEnrichmentPending = createStateHook(Store, {
  selector: (state: State) => {
    const allEnrichmentStatuses = Object.values(state.enrichmentStatus ?? {});
    const firstPendingStatus = allEnrichmentStatuses.find((status) => status === EnrichmentStatus.PENDING);

    return !!firstPendingStatus;
  },
});

export const useAddNewProductStore = createActionsHook(Store);

export const useCloudSiteOrOrgNameMapStore = createHook(Store, {
  selector: (state: State) => getCloudSiteOrOrgNameMap(state, true),
});

export const useProductLineItemsForOrderFailureStore = createHook(Store, {
  selector: (
    state: State,
    props: {
      failureItemIds: string[];
    }
  ) => {
    const allSelectedProducts = Object.values(stitchChangeKeyObjects(state.products, state.selectedProducts));
    const eligibleProducts = allSelectedProducts.filter((product) =>
      props.failureItemIds?.some((id) => {
        return id === product?.id || id === product?.itemId;
      })
    );

    const productsToRender = eligibleProducts;

    return createProductLineItems(state, productsToRender, props.failureItemIds);
  },
});

export const usePartnerPromotionsPayload = createStateHook(Store, {
  selector: (state: State) => {
    const selectedProducts = Object.values(
      stitchChangeKeyObjects<CartProductSchema>(state.products, state.selectedProducts)
    );
    const selectedLinkedEntitlements = getSelectedAppsData(state);
    return {
      selectedPartnerAccountData: state.selectedPartnerAccountData,
      selectedProducts,
      selectedLinkedEntitlements,
      existingPromotions: state.partnerPromotions,
      existingPromotionBenefits: state.partnerPromotionBenefits,
    };
  },
});

export const useProductLineItemsStore = createHook(Store, {
  selector: getProductLineItems,
});

export const useProductAndAppsLineItemsStore = createHook(Store, {
  selector: getProductAndAppsLineItems,
});

export const useLineItemsActualEstimateStore = createHook(Store, {
  selector: getLineItemsActualEstimate,
});

export const useLineItemsRecurringEstimateStore = createHook(Store, {
  selector: getLineItemsRecurringEstimate,
});

export const usePricingPlanOptions = createStateHook(Store, {
  selector: getPricingPlanOptions,
});

export const usePricingPlanEligibility = createStateHook(Store, {
  selector: getPricingPlanEligibility,
});

export const useDestinationBillingProfileDetails = createStateHook(Store, {
  selector: (state: State) => getSelectedBillingProfile(state),
});

function getSelectedProducts(state: State) {
  return Object.values(stitchChangeKeyObjects(state.products, state.selectedProducts));
}

export const useCartProducts = createStateHook(Store, {
  selector: getSelectedProducts,
});

export const useCartApps = createStateHook(Store, {
  selector: (state: State) => getSelectedAppsData(state),
});

export const useProductsForCloudSiteStore = createStateHook(Store, {
  selector: (
    state: State,
    props: {
      cloudSiteHost: string;
    }
  ) => {
    const productsGroupedByCloudSite = groupBy(Object.values(state.products), 'cloudSite');
    return productsGroupedByCloudSite[props.cloudSiteHost] ?? [];
  },
});

export const useProductPromotionsStore = createStateHook(Store, {
  selector: (
    state: State,
    {
      id,
      isApp,
    }: {
      id: string;
      isApp: boolean;
    }
  ) => getAppliedPromotions(state, id, isApp),
});

export const useTransferCloudSiteOrOrgNameMap = createHook(Store, {
  selector: getTransferCloudSiteOrOrgNameMap,
});

export const useOrgProductInstancesMap = createStateHook(Store, {
  selector: getOrgProductInstancesMap,
});

// this hook is required get mapping of cloud sites with only external cloud site products and apps
export const useExternalCloudSiteOrOrgNameMap = createStateHook(Store, {
  selector: getExternalCloudSiteOrOrgNameMap,
});

export const useHasProductMetadataFetchFailed = createStateHook(Store, {
  selector: (state: State) => state.hasProductMetadataFetchFailed,
});

export const useCurrentOperationTypesStore = createStateHook(Store, {
  selector: (state: State) => getCurrentOperationTypesSelector(state),
});

export const useOfferingsAndPricingPlansStore = createStateHook(Store, {
  selector: (state: State) => state.offeringsAndpricingPlans,
});

export const useSelectedProductIdsWithoutChanges = createStateHook(Store, {
  selector: getProductIdsWithoutChanges,
});

export const useIsLatestBillEstimateFetched = createStateHook(Store, {
  selector: getIsLatestBillEstimationFetched,
});

export const useIsSandboxLinkedEntitlementsInCart = createStateHook(Store, {
  selector: getSandboxLinkedEntitlementInCart,
});
